import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { EmailSchema } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodAddInvoiceMailboxPayloadSchema = z.object({
  email: EmailSchema,
});

type AddInvoiceMailboxVariables = {
  companyId: UUID;
  email: string;
};

/**
 * Creates a new invoice mailbox email for a company.
 *
 * @param variables - The variables needed for the mutation.
 * @param variables.companyId - The ID of the company.
 * @param variables.email - The email address to add.
 * @returns A promise that resolves when the invoice mailbox email is created.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/add_company_invoice_mailbox_address_vadmin_company__company_id__invoice_mailbox_post
 */
// fallow-ignore-next-line unused-export
export const addCompanyInvoiceMailbox = async ({
  companyId,
  email,
}: AddInvoiceMailboxVariables) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.INVOICE_MAILBOX.CREATE(companyId), {
        json: { email },
        validate: {
          body: zodAddInvoiceMailboxPayloadSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error adding invoice mailbox',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for adding an invoice mailbox email to a company.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/add_company_invoice_mailbox_address_vadmin_company__company_id__invoice_mailbox_post
 */
export const useMutationCompanyAddInvoiceMailbox = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, AddInvoiceMailboxVariables>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, AddInvoiceMailboxVariables>({
    mutationFn: addCompanyInvoiceMailbox,
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceMailbox(variables.companyId),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
